/**
 * FieldInput — render input mode cho 1 field (cả system + custom).
 * Dùng trong: form gửi đơn (MyRequestsPage) + live preview (TemplateBuilderPage Tab 2).
 *
 * Props:
 *  - field: { id, type, label, required, ... }
 *  - value: any
 *  - onChange: (newValue) => void
 *  - readOnly?: bool
 *  - users?: array — để render user_select / _assignee / _followers
 *  - formData?: full form_data — cần cho formula (tham chiếu field khác)
 *  - formFields?: full form_fields — cần cho formula
 *  - compact?: bool — UI compact hơn
 */
import { useState, useMemo, useEffect } from 'react';
import { ChevronDown, X, Upload, Plus, Trash2, MapPin, Camera, ExternalLink as LucideExternalLink } from 'lucide-react';
import { CURRENCIES, PRIORITY_OPTIONS, FIELD_TYPE_BY_VALUE, FALLBACK_CURRENCY, CRM_RELATION_FIELD_TYPES } from './fieldRegistry';
import { amountInWords, evalFormula, formatFormulaValue } from './utils';
import { optionLabelOf } from './leaveDurationText';
import { uploadSourcesOf, isCameraOnly } from './uploadSources';
import CameraCaptureModal from '../../common/CameraCaptureModal';
import { OwnerPickerMini, FollowersPickerMini } from '../../common/OwnerFollowersPicker';
import SearchableSelect from '../../common/SearchableSelect';
import AsyncSearchableSelect from '../../common/AsyncSearchableSelect';
import Select from '../../common/Select';
import TeamTreeSelect from '../../common/TeamTreeSelect';
import AttendanceLookupCard from '../AttendanceLookupCard';
import LeaveBalanceCard from '../LeaveBalanceCard';
import CrmEntitySmartSearch from '../../common/CrmEntitySmartSearch';
import { EntityRefList } from '../../common/EntityRefDisplay';
import { CRM_ON } from '../../../lib/hostModules';
import api from '@host/axios';

import { Trans, useLingui } from '@lingui/react/macro';
import { tLabel, i18n } from "../../../i18n/runtime.js";
import { msg } from "@lingui/core/macro";

import { fmtNumber, fmtDateTime, fmtYmd, intlLocale } from '../../../i18n/format.js';
import { dateFieldIssue, DATE_INPUT_BOUNDS, DATETIME_INPUT_BOUNDS } from './dateSanity';
import DateIssueHint from './DateIssueHint';

const INP = 'w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-50';

// Ô «Số tiền» hiện số bằng fmtNumber (theo NGÔN NGỮ ĐANG HIỂN THỊ) ⇒ đọc ngược lại cũng phải theo
// đúng dấu nhóm / dấu thập phân của ngôn ngữ đó. Trước 10/10/2026 ghi cứng kiểu vi-VN («.» nhóm,
// «,» thập phân): màn tiếng Anh in «1,234», gõ thêm một số thành «1,2345» → 1.2345 ⇒ không nhập
// nổi số từ 1.000 trở lên. vi-VN cho ra đúng chuỗi xử lý cũ.
function parseAmountInput(text) {
  const parts = new Intl.NumberFormat(intlLocale()).formatToParts(12345.6);
  const group = parts.find(p => p.type === 'group')?.value || '';
  const decimal = parts.find(p => p.type === 'decimal')?.value || '.';
  let s = String(text ?? '');
  if (group) s = s.split(group).join('');
  s = s.replace(/\s/g, '');                                   // fr-CA nhóm bằng khoảng trắng hẹp
  if (decimal !== '.') s = s.replace(/\./g, '').replace(decimal, '.');
  return s
    .replace(/[^\d.-]/g, '')     // bỏ mọi thứ khác
    .replace(/(\..*)\./g, '$1'); // chỉ giữ dấu chấm thập phân đầu tiên
}

/** Dấu thập phân của ngôn ngữ đang hiển thị (cùng nguồn với parseAmountInput). */
function decimalSeparator() {
  return new Intl.NumberFormat(intlLocale()).formatToParts(1.5).find(p => p.type === 'decimal')?.value || '.';
}

/**
 * Ô «Số tiền». Ô có `decimals` > 0 (CAD/USD… 2 số lẻ) giữ đúng chữ đang gõ — «12.» / «12.50» — thay vì
 * định dạng lại mỗi phím bằng fmtNumber (0 số lẻ): trước 12/10/2026 gõ dấu thập phân là bị nuốt, giá trị
 * làm tròn thành số nguyên dù mẫu khai 2 số lẻ. Rời ô thì hiện lại số đã định dạng đủ `decimals` chữ số.
 * Ô 0 số lẻ (VND — Work/Core) đi ĐÚNG đường cũ, không đổi gì.
 */
function AmountInput({ field, value, onChange, readOnly }) {
  const [draft, setDraft] = useState(null);
  const cfg = CURRENCIES.find(c => c.value === (field.currency || FALLBACK_CURRENCY)) || CURRENCIES[0];
  const dec = Math.min(6, Math.max(0, Math.trunc(Number(field.decimals) || 0)));
  const words = field.in_words && value ? amountInWords(value, cfg.value) : '';
  const hasValue = value !== null && value !== undefined && value !== '';
  const display = draft ?? (hasValue ? fmtNumber(Number(value), { d: dec }) : '');

  const handleChange = (text) => {
    // Dấu nhóm / thập phân theo ngôn ngữ đang hiển thị — xem parseAmountInput.
    const raw = parseAmountInput(text);
    if (dec === 0) {
      onChange?.(raw === '' || raw === '-' ? '' : Number(raw));
      return;
    }
    const neg = raw.startsWith('-');
    const [intPart = '', fracRaw] = raw.replace(/-/g, '').split('.');
    const frac = fracRaw === undefined ? undefined : fracRaw.slice(0, dec);
    const intShown = intPart === '' ? (frac !== undefined ? '0' : '') : fmtNumber(Number(intPart));
    setDraft(`${neg ? '-' : ''}${intShown}${frac !== undefined ? decimalSeparator() + frac : ''}`);
    const normalized = `${neg ? '-' : ''}${intPart || '0'}${frac ? `.${frac}` : ''}`;
    onChange?.(intPart === '' && !frac ? '' : Number(normalized));
  };

  return (
    <div>
      <div className="relative">
        <input type="text" value={display} disabled={readOnly} inputMode="decimal"
          onChange={e => handleChange(e.target.value)}
          onBlur={() => setDraft(null)}
          className={`${INP} pr-12`} placeholder="0" />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{cfg.symbol}</span>
      </div>
      {words && (
        <p className="text-[11px] text-gray-500 italic mt-1 px-1">{words}</p>
      )}
    </div>
  );
}

/**
 * Ô ngày + câu báo năm lạ / khoảng đảo ngược (./dateSanity). Câu chỉ hiện khi KHÔNG còn con trỏ
 * trong ô: lúc đang gõ năm, Chrome phát giá trị dở dang (0002 → 0020 → 0202 → 2026), báo ngay thì
 * chữ đỏ nhấp nháy theo từng phím.
 */
function DateGuard({ field, value, children }) {
  const [editing, setEditing] = useState(false);
  return (
    <div onFocus={() => setEditing(true)} onBlur={() => setEditing(false)}>
      {children}
      {!editing && <DateIssueHint issue={dateFieldIssue(field, value)} />}
    </div>
  );
}

/**
 * LockedTimeInput — datetime field với DATE part khóa theo source field.
 * Auto-sync: nếu source date thay đổi, value của field này tự cập nhật
 * date part để giữ time cũ, tránh submit sai ngày.
 */
function LockedTimeInput({ field, value, onChange, readOnly, formData }) {
  let sourceVal = formData?.[field.lock_date_from];
  if (sourceVal && typeof sourceVal === 'object' && 'from' in sourceVal) {
    sourceVal = sourceVal.from;
  }
  const lockedDate = sourceVal && /^\d{4}-\d{2}-\d{2}/.test(String(sourceVal))
    ? String(sourceVal).slice(0, 10) : null;
  const currentTime = value ? String(value).slice(11, 16) : '';

  // Auto-sync date part khi source date thay đổi (user đổi "Ngày cần bổ sung"
  // → "Giờ vào" tự cập nhật date về ngày mới, giữ time cũ).
  useEffect(() => {
    if (!lockedDate || !currentTime || readOnly) return;
    const expected = `${lockedDate}T${currentTime}`;
    if (value !== expected) onChange?.(expected);
  }, [lockedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!lockedDate) {
    return (
      <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
        <Trans>Hãy chọn ngày ở trường nguồn trước khi nhập giờ.</Trans>
      </div>
    );
  }
  // Thứ tự ngày–tháng theo chuẩn của HỆ (Site Connect mm/dd/yyyy, Work/Core dd/mm/yyyy như cũ).
  const ddmm = fmtYmd(lockedDate);
  return (
    <div className="flex items-center gap-2">
      <span className="text-[12px] text-gray-600 font-medium bg-gray-100 border border-gray-200 rounded-lg px-2 py-1 shrink-0">{ddmm}</span>
      <input type="time" value={currentTime}
        onChange={e => onChange?.(e.target.value ? `${lockedDate}T${e.target.value}` : '')}
        disabled={readOnly} className={INP + ' flex-1'} />
    </div>
  );
}

export default function FieldInput({ field, value, onChange, readOnly = false, users = [], formData = {}, formFields = [], onUpdateOther }) {
  const {
    t
  } = useLingui();

  if (!field) return null;
  const { type, id } = field;

  // Ô chọn bản ghi CRM ở hệ TẮT CRM (Site Connect 12/10/2026 — máy chủ chặn `/crm/*`): mẫu cũ còn ô loại này thì
  // chỉ HIỆN giá trị đã lưu (không picker, không gọi /crm/*). Form gửi đơn ẩn hẳn ô — `isCrmFieldOff` bên dưới.
  if (isCrmFieldOff(type)) {
    const ids = Array.isArray(value) ? value.filter(Boolean) : (value ? [value] : []);
    return <EntityRefList fieldType={type} ids={ids} />;
  }

  // ── System fields ──
  if (type === '_title') {
    return (
      <input value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={readOnly}
        placeholder={field.label || t`Nhập tên đề xuất`}
        className="w-full text-2xl font-bold text-gray-900 border-none outline-none bg-transparent placeholder-gray-300 focus:bg-gray-50 rounded px-1 py-1.5" />
    );
  }
  if (type === '_assignee') {
    // Mẫu bật "Chỉ cho phép người gửi đơn" → ô khoá cứng, không mở picker được.
    // Giá trị đã được form pre-fill = người đang đăng nhập; BE cũng chặn lại lần nữa.
    const selfOnly = field.assignee_self_only === true;
    if (selfOnly || readOnly) {
      return (
        <div>
          <OwnerPickerMini value={value || ''} disabled
            lockHint={selfOnly ? i18n._(msg`Mẫu này chỉ cho phép người thực hiện là chính người gửi đơn`) : undefined} />
          {selfOnly && (
            <p className="text-[11px] text-gray-500 mt-1"><Trans>Mẫu này chỉ cho phép người thực hiện là chính bạn.</Trans></p>
          )}
        </div>
      );
    }
    return <OwnerPickerMini value={value || ''} onChange={v => onChange?.(v)} />;
  }
  if (type === '_followers') {
    return <FollowersPickerMini value={value || []} onChange={readOnly ? () => {} : (v => onChange?.(v))} />;
  }
  if (type === '_priority') {
    const cur = value || 'normal';
    return (
      <div className="flex gap-1.5 flex-wrap">
        {PRIORITY_OPTIONS.map(o => (
          <button key={o.value} type="button" disabled={readOnly}
            onClick={() => onChange?.(o.value)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition ${cur === o.value ? o.color : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}>
            {tLabel(o.label)}
          </button>
        ))}
      </div>
    );
  }
  if (type === '_description') {
    return (
      <textarea value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={readOnly}
        rows={3} className={`${INP} resize-none`} placeholder={t`Thêm mô tả...`} />
    );
  }
  if (type === '_attachment') {
    return <FileUploadField value={value || []} onChange={onChange} readOnly={readOnly} multiple max={10} />;
  }

  // ── Layout (no data) ──
  if (type === 'description') {
    return <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed py-1">{field.content || t`Nội dung hướng dẫn...`}</p>;
  }
  if (type === 'section') {
    return <h4 className="text-sm font-bold text-gray-800 pt-3 pb-1 border-b border-gray-200">{field.label}</h4>;
  }
  if (type === 'divider') {
    return <hr className="border-gray-200 my-2" />;
  }

  // ── Custom fields ──
  switch (type) {
    case 'text':
      return (
        <input value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={readOnly}
          className={INP} placeholder={field.placeholder || t`Nhập...`} maxLength={field.max_length || undefined} />
      );

    case 'textarea':
      return <textarea value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={readOnly}
        rows={field.rows || 3} className={`${INP} resize-none`} placeholder={field.placeholder || ''} maxLength={field.max_length || undefined} />;

    case 'number':
      return (
        <div className="relative">
          <input type="number" value={value ?? ''}
            onChange={e => {
              // TC-109: clamp giá trị về field.min nếu nhập âm hoặc nhỏ hơn (vd Số lượng min=1, nhập -3 → set lại 1)
              const raw = e.target.value;
              if (raw === '') return onChange?.('');
              const n = Number(raw);
              if (Number.isFinite(n) && field.min != null && n < field.min) return onChange?.(String(field.min));
              if (Number.isFinite(n) && field.max != null && n > field.max) return onChange?.(String(field.max));
              onChange?.(raw);
            }}
            disabled={readOnly}
            className={INP} min={field.min ?? undefined} max={field.max ?? undefined} step={field.decimals ? Math.pow(10, -field.decimals) : 1}
            placeholder={field.placeholder || '0'} />
          {field.unit && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{field.unit}</span>}
        </div>
      );

    case 'amount':
      return <AmountInput field={field} value={value} onChange={onChange} readOnly={readOnly} />;

    case 'phone':
      return <input type="tel" value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={readOnly}
        className={INP} placeholder={field.placeholder || '0xxx xxx xxx'} />;

    case 'email':
      return <input type="email" value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={readOnly}
        className={INP} placeholder={field.placeholder || 'email@example.com'} />;

    case 'url':
      return <input type="url" value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={readOnly}
        className={INP} placeholder={field.placeholder || 'https://...'} />;

    case 'date': {
      // lock_date_from: chỉ cho user nhập GIỜ, ngày tự lấy từ source field.
      // Dùng cho template "Bổ sung chấm công" — Giờ vào/Giờ ra khóa ngày
      // theo "Ngày cần bổ sung" để user khỏi chọn nhầm ngày khác.
      if (field.with_time && field.lock_date_from) {
        return <LockedTimeInput field={field} value={value} onChange={onChange}
          readOnly={readOnly} formData={formData} />;
      }
      return (
        <DateGuard field={field} value={value}>
          <input type={field.with_time ? 'datetime-local' : 'date'} value={value || ''}
            {...(field.with_time ? DATETIME_INPUT_BOUNDS : DATE_INPUT_BOUNDS)}
            onChange={e => onChange?.(e.target.value)} disabled={readOnly} className={INP} />
        </DateGuard>
      );
    }

    // time_range = legacy alias của date_range (template tăng ca cũ lưu type này).
    // Source hiện tại dùng date_range(with_time); render giống nhau để mọi template
    // legacy không bị "[time_range] — chưa hỗ trợ render" và backend timeOnly parse
    // được datetime. with_time ép true cho time_range (khoảng giờ luôn có phần giờ).
    case 'time_range':
    case 'date_range': {
      const withTime = type === 'time_range' ? true : field.with_time;
      const v = value || {};
      const bounds = withTime ? DATETIME_INPUT_BOUNDS : DATE_INPUT_BOUNDS;
      return (
        <DateGuard field={field} value={value}>
          <div className="grid grid-cols-2 gap-2">
            <input type={withTime ? 'datetime-local' : 'date'} value={v.from || ''} {...bounds}
              onChange={e => onChange?.({ ...v, from: e.target.value })} disabled={readOnly}
              className={INP} />
            <input type={withTime ? 'datetime-local' : 'date'} value={v.to || ''} {...bounds}
              onChange={e => onChange?.({ ...v, to: e.target.value })} disabled={readOnly}
              className={INP} />
          </div>
        </DateGuard>
      );
    }

    case 'attendance_lookup': {
      // Read-only card hiển thị chấm công của user theo ngày user chọn ở date field nguồn.
      // Lưu snapshot DTO vào value (form_data[field.id]) qua onChange → display mode
      // render từ snapshot, KHÔNG fetch lại sau khi đơn duyệt xong.
      // Standalone mode (field.standalone === true): card render date picker tích hợp
      // → cross-write ngày về source_date_field qua onUpdateOther → field date kia ẩn đi.
      const sourceId = field.source_date_field;
      let sourceVal = sourceId ? formData?.[sourceId] : null;
      // date_range field → ưu tiên `from` ngày bắt đầu (use case: nghỉ phép xem chấm công ngày đầu)
      if (sourceVal && typeof sourceVal === 'object' && 'from' in sourceVal) {
        sourceVal = sourceVal.from;
      }
      return (
        <AttendanceLookupCard
          sourceDateValue={sourceVal}
          show={field.show}
          placeholder={field.placeholder}
          standalone={!!field.standalone}
          dateLimit={field.date_limit}
          onPickDate={field.standalone && sourceId && onUpdateOther
            ? (d) => onUpdateOther(sourceId, d)
            : undefined}
          readOnly={readOnly}
          onSnapshot={readOnly ? undefined : (dto) => onChange?.(dto)}
        />
      );
    }

    case 'leave_balance': {
      // Read-only card: số phép còn lại + cảnh báo khi paid không đủ. Đọc leave_type +
      // leave_range cùng form (config qua source_type_field / source_range_field), và CẢ form để
      // thấy ô «Thời gian nghỉ» (Buổi chiều = 0,5) — số ngày tính bằng luật kernel.
      const lt = field.source_type_field ? formData?.[field.source_type_field] : null;
      const rg = field.source_range_field ? formData?.[field.source_range_field] : null;
      return <LeaveBalanceCard leaveType={lt} range={rg} formFields={formFields} formData={formData}
        readOnly={readOnly} onChange={onChange} />;
    }

    case 'select': {
      const labelOf = optionLabelOf(field, formFields);
      return (
        <Select value={value || ''} onChange={onChange} disabled={readOnly}
          options={(field.options || []).map(o => ({ value: o.value, label: labelOf(o) }))}
          placeholder={field.placeholder || t`Chọn...`} />
      );
    }

    case 'multi_select': {
      const arr = Array.isArray(value) ? value : [];
      const opts = field.options || [];
      const labelOf = optionLabelOf(field, formFields);
      return (
        <div className="border border-gray-300 rounded-xl p-2 space-y-1">
          {opts.length === 0 && <p className="text-xs text-gray-400 px-1 py-1"><Trans>Chưa có tùy chọn</Trans></p>}
          {opts.map(o => (
            <label key={o.value} className="flex items-center gap-2 px-1.5 py-1 cursor-pointer hover:bg-gray-50 rounded">
              <input type="checkbox" checked={arr.includes(o.value)} disabled={readOnly}
                onChange={e => {
                  if (e.target.checked) onChange?.([...arr, o.value]);
                  else onChange?.(arr.filter(x => x !== o.value));
                }}
                className="rounded border-gray-300 text-blue-600" />
              <span className="text-sm text-gray-700">{labelOf(o)}</span>
            </label>
          ))}
        </div>
      );
    }

    case 'radio': {
      const labelOf = optionLabelOf(field, formFields);
      return (
        <div className="flex flex-wrap gap-3">
          {(field.options || []).map(o => (
            <label key={o.value} className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name={`r_${id}`} checked={value === o.value} disabled={readOnly}
                onChange={() => onChange?.(o.value)} className="text-blue-600" />
              <span className="text-sm text-gray-700">{labelOf(o)}</span>
            </label>
          ))}
        </div>
      );
    }

    case 'checkbox': {
      return (
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={value === true} disabled={readOnly}
            onChange={e => onChange?.(e.target.checked)}
            className="rounded border-gray-300 text-blue-600" />
          <span className="text-sm text-gray-700">{field.checkbox_label || t`Đồng ý / Bật`}</span>
        </label>
      );
    }

    case 'user_select': {
      if (field.multi) return <FollowersPickerMini value={Array.isArray(value) ? value : []} onChange={onChange} />;
      return <OwnerPickerMini value={value || ''} onChange={onChange} />;
    }

    case 'department_select': {
      // Use SearchableSelect with /teams API
      return <DepartmentPicker value={value} multi={field.multi} onChange={onChange} disabled={readOnly} />;
    }

    case 'image':
    case 'file':
      return <FileUploadField value={value || []} onChange={onChange} readOnly={readOnly}
        multiple={(field.max_files ?? 10) > 1} max={field.max_files ?? 10}
        accept={type === 'image' ? 'image/*' : undefined}
        sources={uploadSourcesOf(field, type === 'image')} />;

    case 'formula': {
      const computed = evalFormula(field.formula, formFields, formData);
      const display = formatFormulaValue(computed, field.formula_format, field.decimals, field.currency);
      return (
        <div className="px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-xl text-sm font-medium text-blue-700">
          {display}
        </div>
      );
    }

    case 'reference':
      return <ReferenceField field={field} value={value} onChange={onChange} disabled={readOnly} />;

    case 'table':
      return <TableField field={field} value={value} onChange={onChange} readOnly={readOnly} />;

    case 'account_select':
      return <RelationPicker endpoint="/crm/accounts" labelKey="name" placeholder={t`Chọn khách hàng...`} value={value} multi={field.multi} onChange={onChange} disabled={readOnly} />;

    case 'company_select':
      return <RelationPicker endpoint="/crm/companies" labelKey="name" placeholder={t`Chọn công ty...`} value={value} multi={field.multi} onChange={onChange} disabled={readOnly} />;

    case 'contact_select':
      return <RelationPicker endpoint="/crm/contacts" labelKey="name" placeholder={t`Chọn liên hệ...`} value={value} multi={field.multi} onChange={onChange} disabled={readOnly} />;

    case 'deal_select':
      return <RelationPicker endpoint="/crm/deals" labelKey="name" placeholder={t`Chọn cơ hội...`} value={value} multi={field.multi} onChange={onChange} disabled={readOnly} />;

    case 'project_select':
      return <RelationPicker endpoint="/tasks/projects" labelKey="name" placeholder={t`Chọn dự án...`} value={value} multi={field.multi} onChange={onChange} disabled={readOnly} />;

    case 'task_select':
      return <RelationPicker endpoint="/tasks" labelKey="title" placeholder={t`Chọn task...`} value={value} multi={field.multi} onChange={onChange} disabled={readOnly} />;

    case 'invoice_select':
      return <RelationPicker endpoint="/crm/invoices" labelKey="invoice_number" placeholder={t`Chọn hoá đơn...`} value={value} multi={field.multi} onChange={onChange} disabled={readOnly} />;

    case 'payment_select':
      return <RelationPicker endpoint="/crm/payments" labelKey="payment_number" placeholder={t`Chọn thanh toán...`} value={value} multi={field.multi} onChange={onChange} disabled={readOnly} />;

    case 'supplier_select':
      return <SupplierSelectField field={field} value={value} onChange={onChange} readOnly={readOnly} />;

    // Backward-compat alias for old data
    case 'customer_select':
      return <RelationPicker endpoint="/crm/accounts" labelKey="name" placeholder={t`Chọn khách hàng...`} value={value} multi={field.multi} onChange={onChange} disabled={readOnly} />;

    case 'location':
      return <LocationField field={field} value={value} onChange={onChange} readOnly={readOnly} />;

    case 'out_evidence':
      return <OutEvidenceField field={field} value={value} onChange={onChange} readOnly={readOnly} />;

    default: {
      const cfg = FIELD_TYPE_BY_VALUE[type];
      return (
        <div className="px-3 py-2 bg-gray-50 text-xs text-gray-400 rounded-xl border border-dashed border-gray-300">[{tLabel(cfg?.label) || type}<Trans>] — chưa hỗ trợ render</Trans>
        </div>
      );
    }
  }
}

// ── Bằng chứng ra ngoài: ảnh minh chứng HOẶC link Lead/Deal (đi gặp khách) ──
// Value: null | [{url,...}] (legacy image list của field file cũ)
//        | { mode: 'image', files: [...] }
//        | { mode: 'crm', entity_type: 'lead'|'deal', entity_id, entity_label, entity_sub }
// Rỗng thì onChange(null) để BE check required hoạt động đúng.
/** Ô chọn bản ghi CRM khi hệ tắt CRM (lib/hostModules) — form gửi đơn ẩn ô + bỏ khỏi kiểm «bắt buộc». */
export function isCrmFieldOff(type) {
  return !CRM_ON && CRM_RELATION_FIELD_TYPES.has(type);
}

function OutEvidenceField({ field, value, onChange, readOnly }) {
  const {
    t
  } = useLingui();

  const legacy = Array.isArray(value);
  const v = legacy ? { mode: 'image', files: value } : (value && typeof value === 'object' ? value : null);
  const [mode, setMode] = useState(v?.mode === 'crm' ? 'crm' : 'image');
  // Value có thể đổ về SAU mount (ResubmitModal prefill qua useEffect) → sync tab theo
  // value, nếu không đơn có link CRM sẽ mở nhầm tab Hình ảnh trống và bị ghi đè câm.
  const valueMode = v?.mode;
  useEffect(() => {
    if (valueMode === 'crm' || valueMode === 'image') setMode(valueMode);
  }, [valueMode]);
  const files = v?.mode === 'image' && Array.isArray(v.files) ? v.files : [];
  const crm = v?.mode === 'crm' && v.entity_id ? v : null;

  const setFiles = (next) => onChange?.(next?.length ? { mode: 'image', files: next } : null);
  const pickItem = (item) => {
    if (!item) return onChange?.(null);
    onChange?.({ mode: 'crm', entity_type: item.entity_type, entity_id: item.id, entity_label: item.label, entity_sub: item.sub || '' });
  };

  const TABS = [
    { key: 'image', label: t`Hình ảnh minh chứng` },
    { key: 'crm', label: t`Gặp khách hàng` },
  ];
  // Hệ tắt CRM (Site Connect): chỉ còn «Hình ảnh minh chứng» — không tab «Gặp khách hàng», không ô tìm lead/deal
  // (`/crm/entities/*`). Đơn cũ đã gắn khách thì vẫn hiện tên đã lưu, chỉ đọc; bỏ chọn là về ảnh.
  const crmOff = !CRM_ON;
  const shownMode = crmOff && !crm ? 'image' : mode;
  return (
    <div className="space-y-2">
      {!readOnly && (!crmOff || crm) && (
        <div className="flex gap-1.5">
          {TABS.map(t => (
            <button key={t.key} type="button" onClick={() => setMode(t.key)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition ${mode === t.key
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}
      {shownMode === 'image' ? (
        <FileUploadField value={files} onChange={setFiles} readOnly={readOnly}
          multiple={(field.max_files ?? 10) > 1} max={field.max_files ?? 10} accept="image/*"
          sources={uploadSourcesOf(field, true)} />
      ) : crmOff ? (
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <span className="truncate">{[crm?.entity_label, crm?.entity_sub].filter(Boolean).join(' · ')}</span>
          {!readOnly && (
            <button type="button" onClick={() => onChange?.(null)} title={t`Bỏ chọn`} className="shrink-0 text-gray-400 hover:text-gray-600">
              <X size={14} />
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-1.5">
          {/* Lead/Deal cho sales; Công ty/Liên hệ cho các bộ phận khác (sếp Phú 2026-08-05). */}
          <CrmEntitySmartSearch
            types={['lead', 'deal', 'company', 'contact']}
            singleEntity
            relatedOnly
            value={{
              lead_id: crm?.entity_type === 'lead' ? crm.entity_id : '',
              deal_id: crm?.entity_type === 'deal' ? crm.entity_id : '',
              company_id: crm?.entity_type === 'company' ? crm.entity_id : '',
              contact_id: crm?.entity_type === 'contact' ? crm.entity_id : '',
            }}
            prefillItems={crm ? [{ entity_type: crm.entity_type, id: crm.entity_id, label: crm.entity_label, sub: crm.entity_sub }] : []}
            onPickItem={readOnly ? undefined : pickItem}
            onChange={() => {}}
            placeholder={t`Tìm lead, deal, công ty, liên hệ theo tên, SĐT...`}
          />
          <p className="text-[11px] text-gray-500 leading-relaxed">
            <Trans>Khi đơn được duyệt, hệ thống tự tạo hoạt động</Trans> <b><Trans>Gặp khách hàng</Trans></b> <Trans>trong khách hàng đã chọn.</Trans>
          </p>
        </div>
      )}
    </div>
  );
}

// Máy cảm ứng (điện thoại / máy tính bảng): trình duyệt tôn trọng `capture` ⇒ mở thẳng camera của máy.
// Máy bàn bỏ qua `capture` (mở hộp chọn tệp = lách được «chỉ chụp ảnh») ⇒ dùng khung chụp webcam.
// Đo con trỏ CHÍNH, không đo «có cảm ứng»: laptop màn cảm ứng vẫn là máy bàn (`capture` vẫn bị bỏ qua).
function prefersNativeCamera() {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)')?.matches;
}

// ── File upload ──
// `sources` = nguồn mẫu đơn cho phép (uploadSourcesOf). «Chỉ chụp ảnh» thì KHÔNG có hộp chọn tệp nào.
function FileUploadField({ value = [], onChange, readOnly, multiple = true, max = 10, accept, sources = ['camera', 'gallery', 'file'] }) {
  const arr = Array.isArray(value) ? value : [];
  const cameraOnly = isCameraOnly(sources);
  // Không có 'file' ⇒ chỉ nhận ảnh, kể cả ô loại «Tệp» mà chủ mẫu đã bỏ tick «Tệp khác».
  const pickAccept = sources.includes('file') ? accept : 'image/*';
  const [nativeCamera] = useState(prefersNativeCamera);
  const [cameraOpen, setCameraOpen] = useState(false);
  const handleFiles = async (files) => {
    if (readOnly) return;
    const list = Array.from(files).slice(0, max - arr.length);
    const uploaded = [];
    for (const f of list) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        const r = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const u = r.data?.data || r.data;
        // persistent_url TRƯỚC url: SaaS trả `url` = signed URL cnv-cdn TTL 15 PHÚT
        // ⇒ lưu vào form_data là file chết sau 15'. Core thì `url` đã bền nên
        // `persistent_url || url` chạy đúng cả hai.
        const fileUrl = u?.persistent_url || u?.url;
        // Câu này hiện ra ô tệp (errorMessage ở catch dưới) ⇒ dịch.
        if (!fileUrl) throw new Error(i18n._(msg`Server không trả về URL file`));
        // Lưu ĐỦ mime + resource_key: URL cnv-cdn KHÔNG có đuôi file nên nhận diện
        // PDF/ảnh (xem inline, lightbox) phải dựa vào mime; resource_key để copy bytes.
        uploaded.push({
          url: fileUrl,
          name: u.original_name || u.name || f.name,
          size: u.size || f.size,
          mime: u.mime_type || u.mime || f.type || undefined,
          resource_key: u.resource_key || u.key || undefined,
        });
      } catch (err) {
        const errText = err?.response?.data?.message || err?.message || i18n._(msg`Upload thất bại`);
        uploaded.push({ url: '', name: f.name, size: f.size, error: true, errorMessage: errText });
      }
    }
    onChange?.([...arr, ...uploaded]);
  };
  return (
    <div className="space-y-1.5">
      {arr.map((f, i) => (
        <div key={i} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs ${f.error ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
          <Upload size={12} className={`shrink-0 ${f.error ? 'text-red-500' : 'text-gray-400'}`} />
          {f.error ? (
            <div className="flex-1 min-w-0">
              <p className="truncate text-red-700 font-medium">⚠ {f.name} <Trans>— upload thất bại</Trans></p>
              {f.errorMessage && <p className="truncate text-[10px] text-red-500 mt-0.5">{f.errorMessage}</p>}
            </div>
          ) : (
            <a href={f.url} target="_blank" rel="noreferrer" className="flex-1 truncate text-gray-700 hover:text-blue-600">{f.name}</a>
          )}
          {!readOnly && (
            <button type="button" onClick={() => onChange?.(arr.filter((_, j) => j !== i))} className={`shrink-0 ${f.error ? 'text-red-400 hover:text-red-600' : 'text-gray-400 hover:text-red-500'}`}>
              <X size={12} />
            </button>
          )}
        </div>
      ))}
      {!readOnly && arr.length < max && !cameraOnly && (
        <label className="flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 text-xs text-gray-500">
          <Upload size={14} /> <Trans>Tải lên</Trans>
          <input type="file" multiple={multiple} accept={pickAccept} className="hidden"
            onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
        </label>
      )}
      {!readOnly && arr.length < max && cameraOnly && (
        <>
          {nativeCamera ? (
            <label className="flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 text-xs text-gray-500">
              <Camera size={14} /> <Trans>Chụp ảnh</Trans>
              <input type="file" accept="image/*" capture="environment" className="hidden"
                onChange={e => { handleFiles(e.target.files); e.target.value = ''; }} />
            </label>
          ) : (
            <button type="button" onClick={() => setCameraOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 text-xs text-gray-500">
              <Camera size={14} /> <Trans>Chụp ảnh</Trans>
            </button>
          )}
          <p className="text-[11px] text-gray-400"><Trans>Mẫu đơn này chỉ nhận ảnh chụp trực tiếp, không chọn ảnh có sẵn.</Trans></p>
          {!nativeCamera && (
            <CameraCaptureModal isOpen={cameraOpen} onClose={() => setCameraOpen(false)}
              onCapture={(file) => { setCameraOpen(false); handleFiles([file]); }} />
          )}
        </>
      )}
    </div>
  );
}

// ── Department picker — cây phòng ban parent-child ──
/**
 * `/teams/simple`, KHÔNG `/teams`: route `/teams` gate quyền `teams:view` mà NHÂN VIÊN
 * THƯỜNG không có ⇒ 403 ⇒ `.catch(()=>{})` nuốt lỗi ⇒ cây rỗng, ô chỉ hiện "Chưa có
 * team nào". Nghĩa là chính người đi gửi đơn không chọn nổi phòng ban, còn admin (system
 * admin, luôn qua gate) thì thấy bình thường nên bug này ẩn rất lâu. Cùng lỗi đã vá ở
 * team-chat/SearchModal.jsx. `/teams/simple` chỉ cần đăng nhập, trả đúng
 * {id,name,parent_id} — vừa đủ dựng cây.
 *
 * limit 1000: `/teams` mặc định phân trang DEFAULT_PAGE_SIZE=20 và `limit:200` cũ vẫn là
 * CẮT NGANG — team bị cắt mà có con thì con thành mồ côi, KHÔNG hiện trong cây.
 */
function DepartmentPicker({ value, multi, onChange, disabled }) {
  const {
    t
  } = useLingui();

  const [teams, setTeams] = useState([]);
  // useEffect, KHÔNG useMemo: useMemo là để tính giá trị, gọi API trong đó là side-effect
  // chạy ngay lúc render (React strict/concurrent có quyền gọi lại hoặc bỏ).
  useEffect(() => {
    let alive = true;
    api.get('/teams/simple', { params: { limit: 1000 } })
      .then(r => { if (alive) setTeams(r.data?.data || r.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (multi) {
    const arr = Array.isArray(value) ? value : [];
    return (
      <TeamTreeSelect teams={teams} selected={arr} onChange={onChange}
        placeholder={t`Chọn phòng ban...`} disabled={disabled} />
    );
  }
  // Single: dùng ĐÚNG chế độ `single` của TeamTreeSelect. Trước đây chạy chế độ multi rồi
  // đoán id mới ("ids.find(id => id !== value)") ⇒ ô hiện checkbox + "Chọn tất cả" + trạng
  // thái nửa-chọn của cha trong khi chỉ được chọn 1, và bấm chọn cha (có nhóm con) thì
  // cascade trả cả cây, phải đoán lấy id nào.
  return (
    <TeamTreeSelect
      single
      teams={teams}
      value={value || null}
      onChange={(id) => onChange?.(id || '')}
      placeholder={t`Chọn phòng ban...`}
      disabled={disabled} />
  );
}

// ── Reference field — tham chiếu đơn approval khác ──
function ReferenceField({ field, value, onChange, disabled }) {
  const {
    t
  } = useLingui();

  const [opts, setOpts] = useState([]);
  useMemo(() => {
    if (!field.ref_template_id) return;
    api.get('/approvals', { params: { template_id: field.ref_template_id, status: 'approved', limit: 50 } })
      .then(r => setOpts((r.data?.data || []).map(x => ({ value: x.id, label: `${x.serial_number || x.id.slice(0, 8)} — ${x.template_name}` })))).catch(() => {});
  }, [field.ref_template_id]);
  return (
    <SearchableSelect value={value || ''} onChange={onChange} options={opts}
      placeholder={field.ref_template_id ? t`Chọn đơn liên quan...` : t`⚠ Chưa cài đặt mẫu nguồn`} disabled={disabled} />
  );
}

// ── Table field — repeating rows ──
function TableField({ field, value, onChange, readOnly }) {
  const rows = Array.isArray(value) ? value : [];
  const cols = field.columns || [];

  const updateRow = (i, colId, v) => {
    const next = [...rows];
    next[i] = { ...next[i], [colId]: v };
    onChange?.(next);
  };
  const addRow = () => onChange?.([...rows, {}]);
  const removeRow = (i) => onChange?.(rows.filter((_, j) => j !== i));

  if (cols.length === 0) {
    return <div className="px-3 py-2 bg-gray-50 text-xs text-gray-400 rounded-xl border border-dashed border-gray-300"><Trans>Chưa có cột nào — cài đặt cột trong builder</Trans></div>;
  }

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {cols.map(c => <th key={c.id} className="text-left px-2.5 py-1.5 text-[11px] font-semibold text-gray-600">{c.label}</th>)}
              {!readOnly && <th className="w-8"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-gray-100">
                {cols.map(c => (
                  <td key={c.id} className="px-1.5 py-1">
                    <FieldInput field={c} value={row[c.id]} onChange={v => updateRow(i, c.id, v)} readOnly={readOnly} />
                  </td>
                ))}
                {!readOnly && (
                  <td className="px-1.5 py-1 text-center">
                    <button type="button" onClick={() => removeRow(i)} className="text-gray-400 hover:text-red-500"><Trash2 size={12} /></button>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={cols.length + (readOnly ? 0 : 1)} className="text-center py-4 text-xs text-gray-400"><Trans>Chưa có dòng nào</Trans></td></tr>
            )}
          </tbody>
        </table>
      </div>
      {!readOnly && (!field.max_rows || rows.length < field.max_rows) && (
        <button type="button" onClick={addRow}
          className="w-full py-2 border-t border-gray-200 text-xs text-blue-600 hover:bg-blue-50 font-medium flex items-center justify-center gap-1">
          <Plus size={12} /> <Trans>Thêm dòng</Trans>
        </button>
      )}
    </div>
  );
}

// ── Generic relation picker (CRM link) — async search debounced ──
function RelationPicker({ endpoint, labelKey = 'name', placeholder, value, multi, onChange, disabled }) {
  if (multi) {
    const arr = Array.isArray(value) ? value : [];
    return <AsyncMultiPicker endpoint={endpoint} labelKey={labelKey} placeholder={placeholder}
      values={arr} onChange={onChange} disabled={disabled} />;
  }
  return <AsyncSearchableSelect searchApi={endpoint} labelField={labelKey}
    value={value || ''} onChange={onChange} placeholder={placeholder} disabled={disabled} />;
}

function AsyncMultiPicker({ endpoint, labelKey, placeholder, values = [], onChange, disabled }) {
  const [labels, setLabels] = useState({}); // id → label cache
  // Load label cho từng id đã chọn (lúc mount)
  useEffect(() => {
    const missing = values.filter(id => !labels[id]);
    if (!missing.length) return;
    Promise.all(missing.map(id =>
      api.get(`${endpoint}/${id}`).then(r => [id, r.data?.data?.[labelKey] || r.data?.data?.name || r.data?.data?.title || id.slice(0, 8)]).catch(() => [id, id.slice(0, 8)])
    )).then(pairs => setLabels(prev => ({ ...prev, ...Object.fromEntries(pairs) })));
  }, [values.join(','), endpoint, labelKey]);

  return (
    <div>
      <AsyncSearchableSelect searchApi={endpoint} labelField={labelKey}
        value="" onChange={(id) => {
          if (id && !values.includes(id)) onChange?.([...values, id]);
        }}
        placeholder={placeholder} disabled={disabled} />
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {values.map(id => (
            <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md text-xs border border-blue-200">
              {labels[id] || id.slice(0, 8)}
              {!disabled && <button type="button" onClick={() => onChange?.(values.filter(x => x !== id))}><X size={10} /></button>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * LocationField — render GPS location.
 * value shape: { latitude, longitude, address, captured_at, status }
 *
 * Admin/desktop tạo đơn: KHÔNG capture GPS được (browser permission UX kém +
 * không phải đơn ngoài VP cho admin). Show empty state với hint "Tự động
 * capture khi tạo trên mobile". User chỉ có thể xoá (set null).
 *
 * Khi field có value (mobile đã capture) → render địa chỉ + tọa độ + nút mở
 * Google Maps để admin verify trực quan.
 */
function LocationField({ field, value, onChange, readOnly }) {
  const {
    t
  } = useLingui();

  const loc = value && typeof value === 'object' ? value : null;
  const lat = loc?.latitude;
  const lng = loc?.longitude;
  const address = loc?.address;
  const capturedAt = loc?.captured_at;

  if (!loc || lat == null || lng == null) {
    return (
      <div className="px-3 py-3 bg-gray-50 border border-dashed border-gray-300 rounded-xl text-xs text-gray-500">
        <div className="flex items-center gap-2 text-gray-400">
          <MapPin size={14} />
          <span><Trans>Chưa có vị trí. Tự động capture khi user tạo đơn trên mobile.</Trans></span>
        </div>
      </div>
    );
  }

  const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
  const fmtTime = capturedAt
    ? fmtDateTime(new Date(capturedAt), {
    preset: "short"
  })
    : null;

  return (
    <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
      <div className="px-3 py-2.5 flex items-start gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
          <MapPin size={16} />
        </div>
        <div className="flex-1 min-w-0">
          {address && (
            <div className="text-sm font-semibold text-gray-900 leading-snug truncate">
              {address}
            </div>
          )}
          <div className="text-xs text-gray-500 mt-0.5 tabular-nums">
            {Number(lat).toFixed(6)}, {Number(lng).toFixed(6)}
            {fmtTime && <span className="ml-2 text-gray-400">· {fmtTime}</span>}
          </div>
        </div>
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 hover:bg-blue-50 rounded-md"
          title={t`Mở Google Maps`}
        >
          <LucideExternalLink size={12} />
          <span><Trans>Bản đồ</Trans></span>
        </a>
      </div>
      {!readOnly && field.allow_manual_edit && (
        <div className="border-t border-gray-100 px-3 py-1.5 bg-gray-50">
          <button
            type="button"
            onClick={() => onChange?.(null)}
            className="text-xs text-red-600 hover:text-red-700 font-medium"
          >
            <Trans>Xoá vị trí</Trans>
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
//  SupplierSelectField — chọn NCC + lưu snapshot vào form_data
// ============================================================
//
// Value shape (single):
//   { id, code, name, snapshot: { code, name, tax_code, address, ... } }
// Snapshot lấy từ GET /suppliers/:id/snapshot — đảm bảo data tại thời điểm
// submit, dù sau đó NCC đổi MST/STK thì đơn cũ vẫn giữ thông tin gốc.
function SupplierSelectField({ field, value, onChange, readOnly }) {
  const {
    t
  } = useLingui();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [Modal, setModal] = useState(null);

  // Lazy-load modal — chỉ load khi user thực sự cần pick.
  const openPicker = async () => {
    if (!Modal) {
      const m = await import('@cnv/ui/pages/suppliers/SupplierPickerModal');
      setModal(() => m.default);
    }
    setPickerOpen(true);
  };

  const handlePick = async (s) => {
    // Gọi snapshot endpoint để get whitelisted fields.
    try {
      const res = await api.get(`/suppliers/${s.id}/snapshot`);
      const snap = res.data?.data?.snapshot || null;
      onChange?.({ id: s.id, code: s.code, name: s.name, snapshot: snap });
    } catch {
      // Fallback: lưu light-data từ picker nếu snapshot fetch fail.
      onChange?.({
        id: s.id,
        code: s.code,
        name: s.name,
        snapshot: {
          code: s.code, name: s.name, tax_code: s.tax_code,
          contact_person: s.contact_person, address: null,
        },
      });
    }
  };

  const v = value || null;

  if (readOnly) {
    if (!v) return <div className="text-sm text-gray-400">—</div>;
    return <SupplierValueCard value={v} />;
  }

  return (
    <>
      {v ? (
        <div className="relative">
          <SupplierValueCard value={v} />
          <button
            type="button"
            onClick={() => onChange?.(null)}
            className="absolute top-2 right-2 p-1 hover:bg-gray-100 rounded-md text-gray-500"
            title={t`Bỏ chọn`}
          >
            <X size={14} />
          </button>
          <button
            type="button"
            onClick={openPicker}
            className="mt-2 text-xs text-blue-600 hover:text-blue-700"
          >
            <Trans>Đổi NCC khác →</Trans>
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          className="w-full text-left px-3 py-2.5 border border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-blue-400 hover:bg-blue-50/30"
        >
          <Trans>+ Chọn nhà cung cấp...</Trans>
        </button>
      )}

      {pickerOpen && Modal && (
        <Modal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onPick={handlePick}
          currentId={v?.id || null}
        />
      )}
    </>
  );
}

function SupplierValueCard({ value }) {
  const snap = value?.snapshot || {};
  return (
    <div className="border border-gray-200 rounded-xl bg-white px-3 py-2.5">
      <div className="flex items-start gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
          <MapPin size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 truncate">
            {snap.name || value.name}
          </div>
          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="font-mono">{snap.code || value.code}</span>
            {snap.tax_code && <span>· MST {snap.tax_code}</span>}
            {snap.contact_person && <span>· {snap.contact_person}</span>}
            {snap.contact_phone && <span>· {snap.contact_phone}</span>}
          </div>
          {snap.bank_account && (
            <div className="text-xs text-gray-500 mt-0.5">
              NH: {snap.bank_name || ''} · STK {snap.bank_account}
              {snap.bank_branch ? ` · ${snap.bank_branch}` : ''}
            </div>
          )}
          {snap.address && (
            <div className="text-xs text-gray-500 mt-0.5 truncate">{snap.address}</div>
          )}
        </div>
      </div>
    </div>
  );
}
